<template>
  <div class="loading_spinner" v-if="loadingChk === false">
    <div class="loading_circle"></div>
  </div>
  <div :id="chartId" ref="chart" v-if="loadingChk === true"></div>
</template>

<script>
import "@/assets/css/clearing.css"
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick, getCurrentInstance } from 'vue';
import zingchart from 'zingchart';
import 'zingchart/modules-es6/zingchart-treemap.min.js';
import { useStore } from 'vuex';
import { mapState } from 'vuex';

export default {
  name: 'ClearingChart',
  setup() {
    const { proxy } = getCurrentInstance();
    const chartId = ref('zingchart-' + Math.random().toString(36).substr(2, 9));
    const store = useStore();
    const clearingData = computed(() => store.state.clearing.clearingData);
    const language = computed(() => store.state.dominance.lang);
    const windowWidth = ref(window.innerWidth);

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
      zingchart.exec(chartId.value, 'resize');
    };
    

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    const getTranslatedText = computed(() => ({
      lev: proxy.$t('liq.lev'),
      nowPrice: proxy.$t('liq.nowPrice'),
      price: proxy.$t('analysis.price'),
      volume: proxy.$t('analysis.volume'),
    }));

    zingchart.TOUCHZOOM = 'none';

    const getLegendLayout = ref(windowWidth.value > 1200 ? 'x5' : 'x2');

    const chartData = ref({
      graphset: [
        {
          type: 'mixed',
          globals: {
            fontFamily: "var(--main-font-Light)",
            fontColor: "var(--color-main)",
            fontSize: '15px',
          },
          'background-color': 'transparent',
          border: 'none',
          title: {
            text: '',
            'font-color': '#7E7E7E',
            backgroundColor: 'none',
            'font-size': '22px',
            alpha: 1,
            'adjust-layout': true,
          },
          plotarea: {
            margin: 'dynamic',
          },
          legend: {
            fontFamily: "Noto Sans JP",
            layout: getLegendLayout.value,
            backgroundColor: 'transparent',
            borderColor: 'none',
            overflow: 'page',
            alpha: 0.1,
            shadow: false,
            align: 'center',
            marker: {
              'border-color': 'none',
              size: '8px',
            },
            item: {
              fontColor: '#FFFFFF',
            },
            'offset-y': -30,
          },
          gui: {
            contextMenu: {
              visible: false,
            },
          },
          plot: {
            animation: {
              effect: 'ANIMATION_SLIDE_BOTTOM',
              sequence: 0,
              speed: 0,
            },
            tooltip: {
              sticky: true,     
              adjustLayout: true, 
            }
          },
          tooltip: {
            fontFamily: "var(--main-font-Light)",
            visible: true,
            text:
              " %t <br />" +
              getTranslatedText.value.price +
              ": $ %kl <br />" +
              getTranslatedText.value.volume +
              ": %v <br />",
            shared: true,
            backgroundColor: 'var(--background-chart2)',
          },
          'scale-y': {
            values: '',
            guide: {
              visible: true,
              lineColor: 'var(--background-list3)',
            },
            multiplier: true,
          },
          'scale-x': {
            values: [],
            guide: {
              visible: false,
            },
            'thousands-separator': ',',
          },
          labels: [
            {
              text: '',
              'background-color': '#FFFFFF',
              'font-size': '13px',
              'font-color': '#000',
              padding: '10%',
              'border-radius': '5px',
              'offset-y': 0,
              height: '25px',
              hook: 'node:plot=4;index=0',
            },
          ],
          series: [
            {
              type: 'bar',
              values: [],
              'background-color': 'var(--chart-red)',
              text: '100x' + getTranslatedText.value.lev,
            },
            {
              type: 'bar',
              values: [],
              'background-color': 'var(--chart-orange1)',
              text: '50x' + getTranslatedText.value.lev,
            },
            {
              type: 'bar',
              values: [],
              'background-color': 'var(--text-green)',
              text: '25x' + getTranslatedText.value.lev,
            },
            {
              type: 'bar',
              values: [],
              'background-color': 'var(--chart-skyblue)',
              text: '10x' + getTranslatedText.value.lev,
            },
            {
              type: 'bar',
              values: [],
              'background-color': '#FFFFFF',
              text: getTranslatedText.value.nowPrice,
              legendMarker: {
                'border-width': 1,
                'border-color': '#FFFFFF',
                type: 'line',
                'line-color': '#FFFFFF',
                size: '6px',
              },
              'guide-label': {
                visible: false,
              },
              tooltip: {
                visible: false,
              },
            },
          ],
        },
      ],
    });

    const renderChart = async () => {
      await nextTick();
      zingchart.render({
        id: chartId.value,
        data: chartData.value,
        height: '500px',
        width: '100%',
      });
      zingchart.exec(chartId.value, 'resize');
      setTimeout(() => {
        removeBtn();
      }, 1000)
    };

    const fontLang = () => {
      if (language.value === 'ja') return "Noto Sans JP";
      if (language.value === 'zh') return "Noto Sans SC";
      return "var(--main-font-Light)";
    };

    const removeBtn = async() => {
      nextTick(() => {
        const toolBar = document.querySelector(`#${chartId.value}-trigger-c`);
        const zingBtn = document.querySelectorAll('[id*="license-text"]');

        if (zingBtn.length > 0 && zingBtn[0]) {
          zingBtn[0].style.display = 'none';
        }
        if (toolBar) toolBar.style.display = 'none';
      });
    };

    const updateChartData = async (newData) => {
      const btcText = Array.isArray(newData.btcPrice[0])
        ? `${newData.btcPrice?.[0]?.[0].toLocaleString()}`
        : '';

      const btcValues = Array.isArray(newData.btcPrice)
        ? (
            Array.isArray(newData.btcPrice[0])
              ? newData.btcPrice
              : newData.btcPrice.map(v => [v])
          )
        : [];

      getLegendLayout.value = windowWidth.value > 1200 ? 'x5' : 'x2';

        // 차트 데이터 객체 업데이트
        chartData.value = {
          ...chartData.value,
          graphset: [
            {
            ...chartData.value.graphset[0],
            legend: { ...chartData.value.graphset[0].legend, layout: getLegendLayout.value },
            'scale-y': { ...chartData.value.graphset[0]['scale-y'], values: newData.volRange },
            'scale-x': { ...chartData.value.graphset[0]['scale-x'], values: newData.priceRange },
            tooltip: { ...chartData.value.graphset[0].tooltip, fontFamily: fontLang() },
            globals: { ...chartData.value.graphset[0].globals, fontFamily: fontLang() },
            labels: [
              {
                ...chartData.value.graphset[0].labels[0],
                text: btcText,
              },
            ],
            series: [
              { ...chartData.value.graphset[0].series[0], values: newData.lev100 },
              { ...chartData.value.graphset[0].series[1], values: newData.lev50 },
              { ...chartData.value.graphset[0].series[2], values: newData.lev25 },
              { ...chartData.value.graphset[0].series[3], values: newData.lev10 },
              { ...chartData.value.graphset[0].series[4], values: btcValues},
            ],
          },
        ],
      };

      // 데이터만 차트에 갱신 (render 다시 호출하지 않음)
      zingchart.exec(chartId.value, 'setdata', { data: chartData.value });
      
      // 필요하면 강제 리사이즈
      zingchart.exec(chartId.value, 'resize');
    };

    const loadAndUpdateChart = async () => {
      await store.dispatch('clearing/loadClearing');
      if (clearingData.value) updateChartData(clearingData.value);
      await removeBtn();
    };

    onMounted(async () => {
      await loadAndUpdateChart();
      renderChart();
      window.addEventListener('resize', handleResize);
      handleResize(); 
    });

    watch(() => store.state.clearing.day, async () => {
      await loadAndUpdateChart();
    });

    watch(clearingData, (newData) => {
      if (newData) updateChartData(newData);
    });

    watch(getTranslatedText, () => {
      chartData.value.graphset[0].series[0].text = '100x' + getTranslatedText.value.lev;
      chartData.value.graphset[0].series[1].text = '50x' + getTranslatedText.value.lev;
      chartData.value.graphset[0].series[2].text = '25x' + getTranslatedText.value.lev;
      chartData.value.graphset[0].series[3].text = '10x' + getTranslatedText.value.lev;
      chartData.value.graphset[0].series[4].text = getTranslatedText.value.nowPrice;
      chartData.value.graphset[0].tooltip.text =
        " %t <br />" +
        getTranslatedText.value.price +
        ": %kl <br />" +
        getTranslatedText.value.volume +
        ": %v <br />";
      renderChart();
    });

    watch(windowWidth, async() => {
      await nextTick();
      await loadAndUpdateChart();
      removeBtn();
    });

    return { chartId };
  },
  computed: {
    ...mapState('clearing', ['loadingChk', 'day']),
  },
};
</script>

<style>
.zingchart-container {
  width: 100%;
  height: 100%;
}
</style>
